// Respawn rounds (Play Now, and game rooms and local games by default; Ranked
// and the Royale stay Classic, where a crash puts you out of the round).
//
// A crash does not end your round: everything drawn in your colour leaves the
// board, and RESPAWN_DELAY seconds later you get a new worm at a free spot,
// holing for RESPAWN_GAP seconds so you can find it. The round ends on a timer
// (gameState.respawn.roundTime seconds after the start phase), not on who is
// left.
//
// Scoring (roundScore): +1 for every worm that crashes into your trail, +1 for
// every ALIVE_POINT_SECONDS alive, -1 for crashing on your own (into your own
// trail or a wall). "Your trail" is your colour: after a Switcharoonie a
// worm's track holds the colours of everyone who drove it, and each segment
// names the player who drew it.
//
// A trail that leaves is not cut out of its track (a stream's deltas and a
// replay address segments by index): its segments get `clearedAt`, the game
// time it went, and a "trail_cleared" game event says so, so a client that
// receives the event applies the same clear (applyTrailClear). Renderers draw
// a segment only while it has not been cleared at their render time.
import * as constants from "./constants.js";
import * as gsf from "./game-state-functions.js";
import * as random from "./util/random.js";
import * as shapeSpatialRelations from "./geometry/shape-spatial-relations.js";
import { createShapeToGridConverter } from "./geometry/shape-to-grid-converter.js";
import { invalidateGridBlocks } from "./util/grid-blocks.js";

export var RESPAWN_DELAY = 2;          // seconds from a crash until you are back in
export var RESPAWN_GAP = 1.5;          // a worm put in starts holing this long
export var ROUND_TIME = 120;           // a Respawn round's length (after the countdown)
export var ALIVE_POINT_SECONDS = 10;   // one point for this many seconds alive

var gridShapes = createShapeToGridConverter();
// (plain ES5 here: core's tests run in an old browser without Math.hypot or Set)
function dist(dx, dy) { return Math.sqrt(dx * dx + dy * dy); }
// (gsf.getPlayer throws for an id it can't place: here a player may have left)
function playerOf(gameState, playerId) {
    for (var i = 0; i < gameState.players.length; i++) {
        if (gameState.players[i].id === playerId) {return gameState.players[i];}
    }
    return null;
}

// Make `gameState` a Respawn round of `roundTime` seconds.
export function enable(gameState, { roundTime = ROUND_TIME, wormSpeed = constants.WORM_SPEED } = {}) {
    gameState.respawn = {
        roundTime: roundTime,
        wormSpeed: wormSpeed, // the round's worm speed (a room can set its own)
        due: {}, // playerId -> the game time they are back in
        out: {}, // playerId -> true: not put back in for now (online: away, or left)
        startPlayers: gameState.players.map(function (p) { return p.id; })
    };
    gameState.endAtAlive = -1; // nobody being left never ends it: the timer does
}

export function isRespawnRound(gameState) {
    return !!(gameState && gameState.respawn);
}

// The game time a Respawn round ends at.
export function endsAt(gameState) {
    return gameState.startPhaseTime + gameState.respawn.roundTime;
}

export function isTimeUp(gameState) {
    return gameState.gameTime >= endsAt(gameState);
}

// A player's last worm crashed (killPlayer): their colour leaves the board and
// they are due back in, unless the round is over by then.
export function onPlayerDied(gameState, playerId) {
    clearPlayerTrail(gameState, playerId);
    var at = gameState.gameTime + RESPAWN_DELAY;
    if (at < endsAt(gameState) && !gameState.respawn.out[playerId]) {
        gameState.respawn.due[playerId] = at;
    }
}

// Keep `playerId` out (`out`: a host's player who is away or left), or let them
// back in: one with no worm then comes in at once.
export function holdOut(gameState, playerId, out) {
    var r = gameState.respawn;
    if (!r) {return;}
    if (out) {
        r.out[playerId] = true;
        delete r.due[playerId];
        return;
    }
    delete r.out[playerId];
    if (!gsf.getAliveWorms(gameState, playerId).length && r.due[playerId] === undefined && !isTimeUp(gameState)) {
        r.due[playerId] = gameState.gameTime;
    }
}

// Put back in everyone who is due (Game.update, every tick). A host whose
// players steer their own worms (online) takes the new worm's first steps from
// the worm_spawned event, like the round's first worms.
export function update(gameState) {
    var due = gameState.respawn.due;
    for (var playerId in due) {
        if (due[playerId] <= gameState.gameTime) {
            delete due[playerId];
            if (playerOf(gameState, playerId)) {
                spawnPlayer(gameState, playerId);
            }
        }
    }
}

// A new worm for `playerId` at a free spot, holing for RESPAWN_GAP: someone
// back in after a crash, or someone new dropping into the round.
export function spawnPlayer(gameState, playerId) {
    var player = playerOf(gameState, playerId);
    player.alive = true;
    var spot = findSpot(gameState);
    var worm = gsf.addWorm(gameState, gsf.createWorm(gameState, {
        playerId: playerId,
        direction: spot.direction,
        centerX: spot.x,
        centerY: spot.y,
        radius: constants.WORM_RADIUS,
        speed: gameState.respawn ? gameState.respawn.wormSpeed : constants.WORM_SPEED
    }));
    worm.jump.remainingJumpTime = RESPAWN_GAP;
    // (its owner's pulses, as at a round's start: the renderers' spawn-events.js).
    // A stream's receivers make the worm from it (online, the worms array is
    // only sent at a round's start), and its owner starts steering it there.
    gameState.gameEvents.push({
        type: "worm_spawned", time: gameState.gameTime, wormId: worm.id, playerId: playerId, trackId: worm.trackId,
        x: worm.centerX, y: worm.centerY, direction: worm.direction, radius: worm.radius,
        speed: worm.speed, turningSpeed: worm.turningSpeed, gap: RESPAWN_GAP
    });
    return worm;
}

// The roomiest of a few random spots: as far as can be from trails, heads and
// walls, heading toward the middle so there is room in front.
export function findSpot(gameState) {
    var map = gameState.map;
    var grid = gameState.playArea.grid;
    var cols = gameState.playArea.cols;
    var rows = gameState.playArea.rows;
    var reach = 90;
    function blocked(x, y) {
        var col = Math.round(x), row = Math.round(y);
        if (col < 0 || row < 0 || col >= cols || row >= rows) {return true;}
        if (grid[row * cols + col] !== constants.PLAY_AREA_FREE) {return true;}
        return !shapeSpatialRelations.contains(map.shape, { centerX: x, centerY: y, radius: constants.WORM_RADIUS });
    }
    function room(x, y) {
        var best = reach;
        for (var a = 0; a < 16; a++) {
            var ang = (a / 16) * Math.PI * 2;
            for (var d = 6; d < best; d += 6) {
                if (blocked(x + Math.cos(ang) * d, y + Math.sin(ang) * d)) { best = d; break; }
            }
        }
        for (var i = 0; i < gameState.worms.length; i++) {
            var w = gameState.worms[i];
            if (w.alive) {best = Math.min(best, dist(w.centerX - x, w.centerY - y) - 30);}
        }
        return best;
    }
    var margin = constants.WORM_RADIUS + 20;
    var pick = { x: map.width / 2, y: map.height / 2, room: -Infinity };
    for (var i = 0; i < 160; i++) {
        var x = margin + random.random(gameState) * (map.width - 2 * margin);
        var y = margin + random.random(gameState) * (map.height - 2 * margin);
        if (blocked(x, y)) {continue;}
        var r = room(x, y);
        if (r > pick.room) {pick = { x: x, y: y, room: r };}
    }
    var direction = Math.atan2(map.height / 2 - pick.y, map.width / 2 - pick.x) + (random.random(gameState) - 0.5) * 0.8;
    return { x: pick.x, y: pick.y, direction: direction };
}

// Everything `playerId` drew leaves the board now, and the "trail_cleared"
// event tells the renderers and a stream's receivers.
export function clearPlayerTrail(gameState, playerId) {
    gameState.gameEvents.push({ type: "trail_cleared", time: gameState.gameTime, playerId: playerId });
    applyTrailClear(gameState, playerId, gameState.gameTime);
}

// Mark `playerId`'s segments from before `time` cleared, and take them off the
// collision grid (when this game state keeps one). A track that keeps other
// colours keeps the cells those cover. Run again for the same clear, it finds
// nothing left to do.
export function applyTrailClear(gameState, playerId, time) {
    var grid = gameState.playArea && gameState.playArea.grid;
    var touched = false;
    for (var key in gameState.wormPathSegments) {
        var segs = gameState.wormPathSegments[key];
        var gone = [];
        for (var i = 0; i < segs.length; i++) {
            var seg = segs[i];
            if (seg.playerId === playerId && seg.clearedAt === undefined && seg.startTime <= time) {
                seg.clearedAt = time;
                gone.push(seg);
            }
        }
        if (!gone.length || !grid) {continue;}
        if (!touched) {
            invalidateGridBlocks(grid);
            touched = true;
        }
        freeCells(gameState, key, segs, gone);
    }
}

// Take the clear of `playerId` at `time` (already marked by applyTrailClear)
// off another grid of the same trails: an online client's own collision grid
// (its cells hold track ids too) is bound as gameState.playArea.grid for this.
export function freeClearedCells(gameState, playerId, time) {
    var grid = gameState.playArea && gameState.playArea.grid;
    if (!grid) {return;}
    for (var key in gameState.wormPathSegments) {
        var segs = gameState.wormPathSegments[key];
        var gone = segs.filter(function (seg) { return seg.playerId === playerId && seg.clearedAt === time; });
        if (gone.length) {freeCells(gameState, key, segs, gone);}
    }
}

// Is `seg` part of the visible trail at `time`? (a renderer's question)
export function isClearedAt(seg, time) {
    return seg.clearedAt !== undefined && seg.clearedAt <= time;
}

// A segment that left a trail behind it.
function leftTrail(seg) {
    return seg.speed > 0 && !seg.jump && seg.type !== "still_arc" && seg.type !== "worm_died" && seg.duration > 0;
}

// Points along a segment every ~`step` units.
function forEachPoint(seg, step, cb) {
    var n;
    if (seg.type === "arc") {
        n = Math.max(1, Math.ceil(Math.abs(seg.arcAngleDiff * seg.arcRadius) / step));
        for (var s = 0; s <= n; s++) {
            var a = seg.arcStartAngle + (s / n) * seg.arcAngleDiff;
            cb(seg.arcCenterX + seg.arcRadius * Math.cos(a), seg.arcCenterY + seg.arcRadius * Math.sin(a));
        }
    } else {
        n = Math.max(1, Math.ceil(dist(seg.endX - seg.startX, seg.endY - seg.startY) / step));
        for (var k = 0; k <= n; k++) {
            cb(seg.startX + (k / n) * (seg.endX - seg.startX), seg.startY + (k / n) * (seg.endY - seg.startY));
        }
    }
}

// The cells the trail paints are its id (`key`, the track's) under circles
// along it (core-functions updateWorms). The gone runs' cells are found the same
// way, a little wider so none is missed; the ones the track's remaining runs
// cover stay.
function freeCells(gameState, key, segs, gone) {
    var playArea = gameState.playArea;
    var grid = playArea.grid;
    var cand = {}, count = 0; // cell -> true
    var minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    gone.forEach(function (seg) {
        if (!leftTrail(seg)) {return;}
        var r = seg.size + 1.5;
        forEachPoint(seg, 1, function (x, y) {
            var cells = gridShapes.convert({ centerX: x, centerY: y, radius: r }, playArea);
            for (var c = 0; c < cells.length; c++) {
                if (grid[cells[c]] === key && !cand[cells[c]]) { cand[cells[c]] = true; count++; }
            }
            if (x - r < minX) {minX = x - r;}
            if (y - r < minY) {minY = y - r;}
            if (x + r > maxX) {maxX = x + r;}
            if (y + r > maxY) {maxY = y + r;}
        });
    });
    if (!count) {return;}
    segs.forEach(function (seg) {
        if (seg.clearedAt !== undefined || !leftTrail(seg)) {return;}
        var r = seg.size + 1;
        forEachPoint(seg, 1, function (x, y) {
            if (x + r < minX || x - r > maxX || y + r < minY || y - r > maxY) {return;}
            var cells = gridShapes.convert({ centerX: x, centerY: y, radius: r }, playArea);
            for (var c = 0; c < cells.length; c++) {cand[cells[c]] = false;}
        });
    });
    for (var cell in cand) {
        if (cand[cell]) {grid[cell] = constants.PLAY_AREA_FREE;}
    }
}

// Whose colour is the trail at grid cell `cell` of track `trackId`: the player
// who drew the segment nearest it (a crash's credit goes to the colour, not to
// whoever drives that worm now).
export function trailPlayerAt(gameState, trackId, cell) {
    var segs = gameState.wormPathSegments[trackId];
    if (!segs) {return undefined;}
    var cols = gameState.playArea.cols;
    var cx = cell % cols, cy = Math.floor(cell / cols);
    var best = Infinity, who;
    for (var i = 0; i < segs.length; i++) {
        var seg = segs[i];
        if (seg.clearedAt !== undefined || !leftTrail(seg)) {continue;}
        forEachPoint(seg, 2, function (x, y) {
            var d = (x - cx) * (x - cx) + (y - cy) * (y - cy);
            if (d < best) { best = d; who = seg.playerId; }
        });
    }
    return who;
}

// Each player's time alive in the round up to `time` (default: now):
// { playerId: { total, longest } } in seconds (longest: their longest life).
export function lives(gameState, time) {
    var t = time === undefined ? gameState.gameTime : time;
    var start = gameState.startPhaseTime;
    var end = Math.min(t, endsAt(gameState));
    var out = {}, since = {};
    function life(id, from, to) {
        var o = out[id] || (out[id] = { total: 0, longest: 0 });
        var d = Math.max(0, to - from);
        o.total += d;
        if (d > o.longest) {o.longest = d;}
    }
    gameState.players.forEach(function (p) { out[p.id] = { total: 0, longest: 0 }; });
    gameState.respawn.startPlayers.forEach(function (id) { since[id] = start; });
    gameState.gameEvents.forEach(function (e) {
        if (e.time > t) {return;}
        if (e.type === "worm_spawned") {
            if (since[e.playerId] === undefined) {since[e.playerId] = Math.max(start, e.time);}
        } else if (e.type === "player_died" && since[e.playerId] !== undefined) {
            life(e.playerId, since[e.playerId], Math.min(e.time, end));
            delete since[e.playerId];
        }
    });
    for (var id in since) {life(id, since[id], end);}
    return out;
}

// Who a crash is credited to: the colour of the trail hit (see trailPlayerAt).
export function crashedInto(e) {
    return e.trailPlayerId !== undefined ? e.trailPlayerId : e.killerPlayerId;
}

// The round's score at `time` (default: now), { playerId: points }.
export function roundScore(gameState, time) {
    var t = time === undefined ? gameState.gameTime : time;
    var score = {}, crashes = {};
    gameState.players.forEach(function (p) { score[p.id] = 0; });
    gameState.gameEvents.forEach(function (e) {
        if (e.time > t || e.type !== "worm_died") {return;}
        var by = crashedInto(e);
        if (e.cause === "wall" || (e.cause === "trail" && by === e.playerId)) {
            crashes[e.playerId] = (crashes[e.playerId] || 0) + 1;
        } else if (by !== undefined) {
            score[by] = (score[by] || 0) + 1;
        }
    });
    var alive = lives(gameState, t);
    for (var pid in score) {
        score[pid] += Math.floor((alive[pid] ? alive[pid].total : 0) / ALIVE_POINT_SECONDS) - (crashes[pid] || 0);
    }
    return score;
}
